import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PreferenceKey, UserPreferenceService } from 'src/app/services/user-preference/user-preference.service';
import { ScopeSequenceStandard } from 'src/app/services/graphql/scope-sequence-standards-response';
import { ScopeSequenceLocalStorageKeys } from './scope-sequence-local-storage-keys';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { GradeSelectingService } from '../../../services/scope-sequence/grade-selecting.service';
import { GradeOption } from '../models/grade-option';
import { GradeOptionSortOrder } from './grade-option-sort-order';

@Component({
  selector: 'scope-sequence-filters',
  templateUrl: './scope-sequence-filters.component.html',
  styleUrls: ['./scope-sequence-filters.component.scss']
})
export class ScopeAndSequenceFilters implements OnInit {
  @Input() public scopeAndSequenceStandards: ScopeSequenceStandard[] = [];
  @Output() updateSkillOrder = new EventEmitter<string>();
  @Output() updateGrade = new EventEmitter<string>();
  @Output() updateSubject = new EventEmitter<string>();

  public subjectSelectedOption: string = '';
  public skillOrderOptions: string[] = [];
  public skillOrderSelectedOption: string = '';
  public gradeOptions: GradeOption[] = [];
  public gradeSelectedOption: string = '';

  constructor(
    private userPreferenceService: UserPreferenceService,
    private localStorageService: LocalStorageService,
    private gradeSelectingService: GradeSelectingService
  ) { }

  public async ngOnInit(): Promise<void> {
    this.setSelectedSubject();
    await this.setSkillOrderOptions();
  }

  public updateGradeSelection(value: string) {
    this.gradeSelectedOption = value;
    this.updateGrade.emit(value);
    this.localStorageService.setItem(ScopeSequenceLocalStorageKeys.Grade, value);
  }

  public async updateSkillOrderSelection(value: string) {
    this.skillOrderSelectedOption = value;
    // the grade options depend on the skill order, so we need to update them
    this.setGradeOptions();
    await this.setSelectedGrade();

    this.updateSkillOrder.emit(value);
    this.localStorageService.setItem(ScopeSequenceLocalStorageKeys.Skill, value);
  }

  public updateSubjectSelection(value: string) {
    this.updateSubject.emit(value);
  }

  public setSelectedSubject(): void {
    this.subjectSelectedOption = 'Math';
  }

  public async setSkillOrderOptions(): Promise<void> {
    const standards = this.scopeAndSequenceStandards;

    const isSavvasPreferenceEnabledString = await this.userPreferenceService.getPreferenceValue(PreferenceKey.SavvasEnabled);
    const isSavvasPreferenceEnabled = isSavvasPreferenceEnabledString.toLowerCase() === 'true';

    let standardSetIds: string[] = [];

    standardSetIds = standards.filter(x => !x.standardSetId.toLowerCase().includes('envision') || isSavvasPreferenceEnabled).map(x => x.standardSetId);

    //remove duplicates
    standardSetIds = [...new Set(standardSetIds)];

    this.skillOrderOptions = standardSetIds;

    const selectedStandardSet = this.localStorageService.getItem(ScopeSequenceLocalStorageKeys.Skill);
    if (selectedStandardSet) {
      this.skillOrderSelectedOption = selectedStandardSet;
      await this.updateSkillOrderSelection(this.skillOrderSelectedOption);
      return;
    }

    this.skillOrderSelectedOption = this.skillOrderOptions[0];
    await this.updateSkillOrderSelection(this.skillOrderSelectedOption);
  }

  private setGradeOptions(): void {
    let grades = this.scopeAndSequenceStandards
      .filter(standard => standard.standardSetId === this.skillOrderSelectedOption)
      .map(standard => standard.collectionLongName)
      .sort((a, b) => {
        const indexA = GradeOptionSortOrder.indexOf(a);
        const indexB = GradeOptionSortOrder.indexOf(b);
        return indexA - indexB;
      });

    // Remove duplicates
    grades = [...new Set(grades)];

    this.gradeOptions = grades.map(grade => {
      return {
        displayName: grade.replace('Grade ', ''),
        value: grade
      };
    });
  }

  public async setSelectedGrade(): Promise<void> {
    const storedGrade = this.localStorageService.getItem(ScopeSequenceLocalStorageKeys.Grade);
    if (storedGrade && this.gradeOptions.some(x => x.value === storedGrade)) {
      this.gradeSelectedOption = storedGrade;
      this.updateGradeSelection(this.gradeSelectedOption);
      return;
    }

    let mostCommonGrade = await this.gradeSelectingService.getMostCommonStudentGrade();

    //hack because student grades are numbers, and standardset grades have a leading 'Grade '
    mostCommonGrade = 'Grade ' + mostCommonGrade;

    if (this.gradeOptions.some(x => x.value === mostCommonGrade)) {
      this.gradeSelectedOption = mostCommonGrade;
      this.updateGradeSelection(this.gradeSelectedOption);
      return;
    }

    this.gradeSelectedOption = this.gradeOptions[0].value;
    this.updateGradeSelection(this.gradeSelectedOption);
  }
}
